import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, ArrowLeft, Database, AlertCircle, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import logo from '../assets/logo.png';
import { useI18n } from '../i18n/i18n';

interface LoginPageProps {
  onLoginSuccess: () => void;
  onBackClick: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, onBackClick }) => {
  const { t } = useI18n()
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSignUp, setShowSignUp] = useState(false);
  const [suFirst, setSuFirst] = useState('');
  const [suLast, setSuLast] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPassword, setSuPassword] = useState('');
  const [suConfirm, setSuConfirm] = useState('');
  const [suShowPassword, setSuShowPassword] = useState(false);
  const [suLoading, setSuLoading] = useState(false);
  const [suError, setSuError] = useState<string | null>(null);
  const [suSuccess, setSuSuccess] = useState<string | null>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(error.message)
    } else {
      onLoginSuccess();
    }
    setLoading(false);
  };

  const handleSignup = () => {
    setShowSignUp(true)
  }

  const handleReset = async () => {
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `https://dash.dataevolution-demo.com/auth/reset`
    })
    setLoading(false)
    if (error) setError(error.message)
  }

  // Sign up submit
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuError(null)
    setSuSuccess(null)
    if (!suFirst || !suLast || !suEmail || !suPassword || !suConfirm) {
      setSuError('Please fill all fields.')
      return
    }
    if (suPassword !== suConfirm) {
      setSuError('Passwords do not match.')
      return
    }
    setSuLoading(true)
    const { error } = await supabase.auth.signUp({
      email: suEmail,
      password: suPassword,
      options: {
        emailRedirectTo: 'https://dash.dataevolution-demo.com',
        data: {
          first_name: suFirst,
          last_name: suLast,
          display_name: `${suFirst} ${suLast}`.trim()
        }
      }
    })
    setSuLoading(false)
    if (error) {
      setSuError(error.message)
    } else {
      setSuSuccess('Account created. Check your email to verify your address.')
      setShowSignUp(false)
      setShowVerifyModal(true)
    }
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={onBackClick}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('login.back')}
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img src={logo} alt="Data Evolution logo" className="max-h-14 w-auto" />
              <h1 className="text-2xl font-bold text-gray-900">{t('login.title')}</h1>
            </div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">{t('login.welcome')}</h2>
            <p className="text-gray-500">{t('login.subtitle')}</p>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="p-1 bg-blue-100 rounded">
                <Database className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1 text-sm text-blue-700">
                Use your email and password. If you don't have an account yet, click "Create account" below.
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">{t('login.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">{t('login.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>{t('login.signin')}</span>
                </div>
              ) : (
                t('login.signin')
              )}
            </button>

            {/* Signup Button */}
            <button
              type="button"
              onClick={handleSignup}
              disabled={loading}
              className="w-full mt-2 bg-white text-primary-700 border border-primary-200 py-3 px-4 rounded-lg hover:bg-primary-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {t('login.create')}
            </button>

            {/* Reset password */}
            <button
              type="button"
              onClick={handleReset}
              disabled={loading || !email}
              className="w-full mt-2 bg-white text-gray-700 border border-gray-200 py-3 px-4 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {t('login.reset')}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              Data Evolution Platform v1.0.0
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Secure access to infrastructure management
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Lock className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-white">Secure Connection</span>
          </div>
          <p className="text-xs text-gray-400">
            Your connection is encrypted and secure
          </p>
        </div>
      </div>
    </div>
    {showSignUp && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Create your account</h3>
            </div>
            <button onClick={() => setShowSignUp(false)} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>
          {suError && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 mb-3">{suError}</div>}
          {suSuccess && <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-700 mb-3">{suSuccess}</div>}
          <form onSubmit={handleSignupSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">First name</label>
                <input value={suFirst} onChange={(e) => setSuFirst(e.target.value)} className="w-full border rounded px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Last name</label>
                <input value={suLast} onChange={(e) => setSuLast(e.target.value)} className="w-full border rounded px-3 py-2" required />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Email</label>
              <input type="email" value={suEmail} onChange={(e) => setSuEmail(e.target.value)} className="w-full border rounded px-3 py-2" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <label className="block text-sm text-gray-700 mb-1">Password</label>
                <input type={suShowPassword ? 'text' : 'password'} value={suPassword} onChange={(e) => setSuPassword(e.target.value)} className="w-full border rounded px-3 py-2 pr-10" required />
                <button type="button" onClick={() => setSuShowPassword(!suShowPassword)} className="absolute right-3 top-8 text-gray-400 hover:text-gray-600">
                  {suShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Confirm password</label>
                <input
                  type={suShowPassword ? 'text' : 'password'}
                  value={suConfirm}
                  onChange={(e) => setSuConfirm(e.target.value)}
                  className={`w-full rounded px-3 py-2 border ${suConfirm && suPassword && suConfirm !== suPassword ? 'border-red-500 focus:ring-2 focus:ring-red-500' : 'border-gray-300'}`}
                  required
                />
              </div>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2">
              <button type="button" onClick={() => setShowSignUp(false)} className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={suLoading} className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                {suLoading ? 'Creating…' : 'Create account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    {showVerifyModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center space-x-2 mb-2">
            <Mail className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Verify your email</h3>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            We sent a confirmation email to <span className="font-medium">{suEmail}</span>.
            Please click the link in the email to activate your account.
          </p>
          <div className="text-right">
            <button onClick={() => setShowVerifyModal(false)} className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">OK</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default LoginPage;





